// Shared date / colour helpers for the new top-level Appointments calendar.
// Kept tiny + pure so the WeekGrid + StaffRail + future Day/Month/Persons
// views can all consume the same primitives without re-implementing them.

export const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Parse an appointment `start_at` / `end_at` value as clinic WALL-CLOCK
 * time — NOT as UTC.
 *
 * Appointments carry the front-desk's local time (IST). Some legacy rows
 * in Mongo still have a `+00:00` suffix from a pre-fix write path (the
 * backend used to stamp naive datetimes with UTC before storing). If we
 * feed such a string to `new Date(...)`, the browser adds another +5:30
 * on IST devices, so an 11:15 booking ends up rendered at 16:45 on the
 * calendar grid. Stripping the tz suffix BEFORE `new Date` makes the
 * value parse as local, so 11:15 always renders at 11:15 regardless of
 * whether the row was written pre- or post-fix.
 *
 * Also accepts:
 *   • Date objects — returned as-is.
 *   • Values with a trailing `Z` or `-HH:MM` offset — same treatment,
 *     strip then re-parse locally. If the caller genuinely intended UTC
 *     they should not be routing through this helper.
 *
 * @param {string | Date | null | undefined} v
 * @returns {Date} local wall-clock Date (may be Invalid Date if `v` is malformed).
 */
export const parseWallClock = (v) => {
  if (v instanceof Date) return v;
  if (v == null) return new Date(NaN);
  if (typeof v !== 'string') return new Date(v);
  // Strip a trailing tz suffix (`+HH:MM`, `-HH:MM`, or `Z`) leaving the
  // date-time portion intact. `new Date("YYYY-MM-DDTHH:MM:SS")` (naive)
  // is interpreted as local by every modern engine, which is exactly
  // what we want for clinic wall-clock times.
  const naive = v.replace(/([+\-]\d{2}:?\d{2}|Z)$/, '');
  return new Date(naive);
};

/** Mon-anchored start of the ISO week containing `d`. */
export const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0 Sun … 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
};

export const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const fmtRange = (start, end) => {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sM = start.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const eM = end.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  if (sameMonth) {
    return `${start.getDate()} – ${end.getDate()} ${end.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${sM} – ${eM}`;
};

export const fmtTimeShort = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
};

/** Convert a full ISO timestamp to "HH:MM" (24h) using the local timezone. */
export const isoToHHMM = (iso) => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Return contrast colour (#000 or #fff) for a hex background; lifted from W3C YIQ. */
export const contrastOn = (hex) => {
  if (!hex) return '#fff';
  const c = hex.replace('#', '');
  const v = c.length === 3
    ? c.split('').map((ch) => parseInt(ch + ch, 16))
    : [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16));
  if (v.some(Number.isNaN)) return '#fff';
  const yiq = (v[0] * 299 + v[1] * 587 + v[2] * 114) / 1000;
  return yiq >= 150 ? '#0F172A' : '#FFFFFF';
};

/** Format a Date as local YYYY-MM-DD (avoids UTC shift from toISOString). */
export const ymdLocal = (d) => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/**
 * Format a Date as naive-local `YYYY-MM-DDTHH:MM:SS` (no tz suffix, no Z).
 *
 * Appointments carry clinic wall-clock times, not UTC. `.toISOString()`
 * converts local → UTC and the round-trip through the browser adds a
 * +5:30 offset on IST devices, which was rendering the booked slot
 * ~5 hours later than the user picked. Emitting a naive string keeps
 * the value 1:1 with what the front-desk selected.
 */
export const naiveLocalIso = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${yy}-${mm}-${dd}T${hh}:${mi}:${ss}`;
};

export const hexAlpha = (hex, a = 0.15) => {
  if (!hex) return `rgba(15,23,42,${a})`;
  const c = hex.replace('#', '');
  const v = c.length === 3
    ? c.split('').map((ch) => parseInt(ch + ch, 16))
    : [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16));
  if (v.some(Number.isNaN)) return `rgba(15,23,42,${a})`;
  return `rgba(${v[0]},${v[1]},${v[2]},${a})`;
};
